import { useState, useEffect, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Camera, 
  Search,
  Calendar,
  Image,
  CreditCard,
  FileSignature,
  User,
  ChevronRight,
  ChevronDown,
  ChevronLeft,
  Mail,
  HelpCircle,
  Rocket,
  Settings,
  DollarSign,
  ArrowLeft,
  CheckCircle,
  Lightbulb,
  Clock,
  Upload,
  Eye,
  Lock,
  Globe,
  Percent,
  Star,
  Zap,
  Shield,
  Link as LinkIcon,
  MessageSquare,
  Send,
  FileText,
  Download,
  Share2,
  Palette,
  Bell,
  Receipt,
  UserPlus,
  Target,
  Award,
  ArrowRight,
  Crown
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { useAuth, API } from "../App";
import axios from "axios";

// Visual help content with better formatting
const HELP_ARTICLES = {
  "getting-started": {
    title: "Getting Started Guide",
    icon: <Rocket className="w-6 h-6" />,
    color: "text-primary",
    bgColor: "bg-primary/10",
    steps: [
      {
        title: "Welcome to 360Lumina!",
        icon: <Star className="w-5 h-5" />,
        highlights: [
          { icon: <Zap />, text: "14-day Pro trial included" },
          { icon: <Image />, text: "Unlimited galleries during trial" },
          { icon: <Calendar />, text: "Google Calendar sync" },
          { icon: <Palette />, text: "Watermarking & more" }
        ],
        tip: "After your 14-day trial, you'll be automatically billed at your currently subscribed tier. Manage your subscription anytime in Settings."
      },
      {
        title: "Complete Your Profile",
        icon: <User className="w-5 h-5" />,
        checklist: [
          { label: "Brand Name", desc: "Becomes your public URL", required: true },
          { label: "Profile Photo", desc: "Professional headshot or logo" },
          { label: "Tagline", desc: "Short, memorable phrase" },
          { label: "Bio", desc: "2-3 paragraphs about you" },
          { label: "Social Links", desc: "Instagram, Facebook, etc." }
        ],
        action: { label: "Edit Profile", link: "/dashboard/profile" }
      },
      {
        title: "Set Up Your Portfolio",
        icon: <Image className="w-5 h-5" />,
        numbered: [
          "Go to Dashboard → Galleries",
          "Click \"Create Gallery\"",
          "Name it \"Portfolio\"",
          "Upload your best 10-20 images",
          "Set as your Portfolio Album in Profile"
        ],
        tip: "Quality over quantity - show your absolute best work!"
      },
      {
        title: "Set Your Availability",
        icon: <Calendar className="w-5 h-5" />,
        numbered: [
          "Go to Dashboard → Calendar",
          "Click on dates to add availability",
          "Set your available hours",
          "Connect Google Calendar (optional)"
        ],
        highlight: {
          icon: <Zap />,
          title: "Pro Tip: Google Calendar Sync",
          text: "Bookings automatically appear in your Google Calendar!"
        }
      },
      {
        title: "Connect Payments",
        icon: <CreditCard className="w-5 h-5" />,
        features: [
          { icon: <CreditCard />, text: "Accept credit/debit cards" },
          { icon: <DollarSign />, text: "Sell individual photos" },
          { icon: <Receipt />, text: "Send professional invoices" },
          { icon: <Shield />, text: "Secure Stripe processing" }
        ],
        action: { label: "Connect Stripe", link: "/dashboard/payments" },
        note: "You can skip this and add it later - your profile will still work!"
      }
    ]
  },
  
  "calendar": {
    title: "Calendar & Scheduling",
    icon: <Calendar className="w-6 h-6" />,
    color: "text-blue-400",
    bgColor: "bg-blue-500/10",
    steps: [
      {
        title: "Managing Availability",
        icon: <Clock className="w-5 h-5" />,
        cards: [
          {
            title: "Adding Time Slots",
            items: ["Click any date on calendar", "Add available hours", "Save changes"]
          },
          {
            title: "Blocking Time Off",
            items: ["Click the date", "Select \"Block this date\"", "Add optional note"]
          }
        ],
        tip: "Blocked dates won't show as available to clients."
      },
      {
        title: "Google Calendar Sync",
        icon: <Globe className="w-5 h-5" />,
        numbered: [
          "Go to Dashboard → Calendar",
          "Click \"Connect Google Calendar\"",
          "Sign in with Google",
          "Select which calendar to sync"
        ],
        features: [
          { icon: <Download />, text: "Import existing events as blocked time" },
          { icon: <Upload />, text: "Auto-sync confirmed bookings" },
          { icon: <ArrowRight />, text: "Two-way updates" }
        ]
      },
      {
        title: "Handling Booking Requests",
        icon: <MessageSquare className="w-5 h-5" />,
        options: [
          { icon: <CheckCircle />, label: "Confirm", desc: "Accept the booking", color: "text-green-400" },
          { icon: <Send />, label: "Send Quote", desc: "Provide pricing first", color: "text-blue-400" },
          { icon: <MessageSquare />, label: "Request Info", desc: "Ask for more details", color: "text-yellow-400" }
        ],
        tip: "Respond within 24-48 hours for best client experience."
      }
    ]
  },
  
  "galleries": {
    title: "Galleries & Delivery",
    icon: <Image className="w-6 h-6" />,
    color: "text-green-400",
    bgColor: "bg-green-500/10",
    steps: [
      {
        title: "Creating Galleries",
        icon: <Image className="w-5 h-5" />,
        numbered: [
          "Dashboard → Galleries → \"Create Gallery\"",
          "Enter title and description",
          "Choose visibility (Private/Public)",
          "Link to a booking (optional)"
        ],
        options: [
          { icon: <Lock />, label: "Private", desc: "Only via magic link" },
          { icon: <Globe />, label: "Public", desc: "Visible on your profile" }
        ]
      },
      {
        title: "Uploading Photos",
        icon: <Upload className="w-5 h-5" />,
        specs: [
          { label: "Formats", value: "JPEG, PNG, WebP" },
          { label: "Max size", value: "50MB per file" },
          { label: "Recommended", value: "2048px longest edge" }
        ],
        tip: "Upload in batches of 20-30 for best results. Don't close browser during upload!"
      },
      {
        title: "Storage & Fair Usage",
        icon: <Shield className="w-5 h-5" />,
        specs: [
          { label: "Photo uploads", value: "Plan-based session limits" },
          { label: "Video hosting", value: "Time-based (30-120 min by plan)" },
          { label: "Founder bonus", value: "+15-30 video minutes" }
        ],
        options: [
          { icon: <Image />, label: "Starter", desc: "5 galleries, 30 min video" },
          { icon: <Image />, label: "Pro", desc: "Unlimited galleries, 60 min video" },
          { icon: <Image />, label: "Studio", desc: "Unlimited galleries, 120 min video" }
        ],
        tip: "360Lumina uses fair usage policies to ensure reliable performance for all photographers. No truly 'unlimited' storage."
      },
      {
        title: "Delivering to Clients",
        icon: <Send className="w-5 h-5" />,
        numbered: [
          "Open the gallery",
          "Click \"Deliver to Client\"",
          "Enter client's email",
          "Add a personal message",
          "Click \"Send\""
        ],
        highlight: {
          icon: <Shield />,
          title: "Security",
          text: "Magic links are unique per gallery. Set expiration dates for time-limited access."
        }
      },
      {
        title: "Watermarking",
        icon: <Palette className="w-5 h-5" />,
        badge: "Pro+",
        options: [
          { icon: <FileText />, label: "Text", desc: "Your name, copyright, etc." },
          { icon: <Image />, label: "Logo", desc: "PNG with transparency" }
        ],
        settings: [
          "Position: 9 placement options",
          "Opacity: 10-100%",
          "Size: Small, Medium, Large"
        ],
        action: { label: "Set Up Watermarks", link: "/dashboard/watermark" }
      }
    ]
  },
  
  "payments": {
    title: "Payments & Sales",
    icon: <CreditCard className="w-6 h-6" />,
    color: "text-yellow-400",
    bgColor: "bg-yellow-500/10",
    steps: [
      {
        title: "Connecting Stripe",
        icon: <CreditCard className="w-5 h-5" />,
        numbered: [
          "Dashboard → Payments",
          "Click \"Connect with Stripe\"",
          "Create/sign in to Stripe",
          "Complete identity verification",
          "Add your bank account"
        ],
        specs: [
          { label: "First payout", value: "7-14 days" },
          { label: "Regular payouts", value: "2 business days" },
          { label: "Platform fee", value: "3-5% (inclusive of processing)" }
        ],
        tip: "360Lumina's platform fee includes Stripe processing. You're never charged a separate Stripe fee on top of the platform fee."
      },
      {
        title: "Quotes, Invoices & Payments",
        icon: <Receipt className="w-5 h-5" />,
        highlight: {
          icon: <ArrowRight />,
          title: "Standard Flow",
          text: "Quote → Client Accepts → Invoice Generated Automatically"
        },
        cards: [
          {
            title: "From a Quote",
            items: ["Create & send quote to client", "Client reviews and accepts", "Invoice is auto-generated", "Client pays via invoice link"]
          },
          {
            title: "Ad-hoc Charges",
            items: ["Dashboard → Create Ad-hoc Charge", "Enter client email & amount", "Describe the service", "Send directly (no quote needed)"]
          }
        ],
        features: [
          { icon: <Bell />, text: "Automatic payment reminders" },
          { icon: <CreditCard />, text: "Online payment via Stripe" },
          { icon: <Download />, text: "PDF download option" }
        ]
      },
      {
        title: "Selling Photos",
        icon: <DollarSign className="w-5 h-5" />,
        numbered: [
          "Open a gallery",
          "Go to Settings → Sales",
          "Toggle \"Enable Photo Sales\"",
          "Set per-photo price",
          "Optionally add packages"
        ],
        tip: "Research your market and consider tiered pricing for better conversions."
      },
      {
        title: "Promo Codes",
        icon: <Percent className="w-5 h-5" />,
        badge: "Pro+",
        numbered: [
          "Dashboard → Promo Codes",
          "Click \"Create Code\"",
          "Set code name (e.g., SUMMER20)",
          "Choose discount type",
          "Set optional limits"
        ],
        options: [
          { icon: <Percent />, label: "Percentage", desc: "e.g., 20% off" },
          { icon: <DollarSign />, label: "Fixed Amount", desc: "e.g., $50 off" }
        ]
      }
    ]
  },
  
  "contracts": {
    title: "Contracts & E-Signatures",
    icon: <FileSignature className="w-6 h-6" />,
    color: "text-purple-400",
    bgColor: "bg-purple-500/10",
    steps: [
      {
        title: "Creating Templates",
        icon: <FileText className="w-5 h-5" />,
        badge: "Studio",
        numbered: [
          "Dashboard → Contracts → \"Create Template\"",
          "Choose starting template or blank",
          "Edit the content",
          "Use placeholders for dynamic content",
          "Save the template"
        ],
        placeholders: [
          "{{client_name}}",
          "{{photographer_name}}",
          "{{event_date}}",
          "{{event_type}}",
          "{{date}}"
        ],
        tip: "Have a lawyer review your contracts for legal compliance."
      },
      {
        title: "Sending Contracts",
        icon: <Send className="w-5 h-5" />,
        numbered: [
          "Dashboard → Contracts",
          "Select a template",
          "Click \"Send to Client\"",
          "Enter client email",
          "Link to a booking",
          "Send"
        ],
        statuses: [
          { icon: <Clock />, label: "Sent", desc: "Waiting for signature", color: "text-blue-400" },
          { icon: <CheckCircle />, label: "Signed", desc: "Client has signed", color: "text-green-400" }
        ]
      }
    ]
  },
  
  "account": {
    title: "Account & Settings",
    icon: <Settings className="w-6 h-6" />,
    color: "text-orange-400",
    bgColor: "bg-orange-500/10",
    steps: [
      {
        title: "Subscription Plans",
        icon: <Award className="w-5 h-5" />,
        dynamic_plans: true, // Flag to indicate this should use dynamic plan data
        action: { label: "Manage Subscription", link: "/dashboard/billing" }
      },
      {
        title: "Profile Updates",
        icon: <User className="w-5 h-5" />,
        checklist: [
          { label: "Portfolio", desc: "Add recent work regularly" },
          { label: "Bio", desc: "Reflect current experience" },
          { label: "Availability", desc: "Keep calendar current" },
          { label: "Photo", desc: "Update annually" }
        ],
        warning: "Changing your brand name will change your profile URL. Old links will stop working."
      }
    ]
  }
};

// FAQs organized by category
const PHOTOGRAPHER_FAQS = [
  {
    category: "Getting Started",
    icon: <Rocket className="w-4 h-4" />,
    questions: [
      { q: "How do I set my availability for bookings?", a: "Go to Dashboard → Calendar to set your available time slots. Click on any date to add or remove availability. You can also connect Google Calendar for automatic two-way sync." },
      { q: "What happens when my trial ends?", a: "After your 14-day trial, you'll be automatically billed at your currently subscribed tier. You can change or cancel your subscription anytime before the trial ends in Dashboard → Billing." },
      { q: "Can I change my profile URL?", a: "Only before you publish for the first time. After publishing, your URL becomes permanent to prevent broken links." }
    ]
  },
  {
    category: "Payments",
    icon: <CreditCard className="w-4 h-4" />,
    questions: [
      { q: "How do I get paid?", a: "Connect your Stripe account in Dashboard → Payments. Once connected, you can create invoices, accept payments, and sell photos. Payments go directly to your bank account." },
      { q: "What are the fees?", a: "360Lumina's platform fee is inclusive of Stripe processing fees. You are never charged a separate Stripe fee on top of the platform fee. Platform fees by tier: Starter (5%), Pro (3%), Studio (3%)." },
      { q: "How does the quote and invoice process work?", a: "The flow is: 1) Create & send a Quote, 2) Client accepts the Quote, 3) An Invoice is automatically generated. You can also create ad-hoc charges for additional services outside of quotes." }
    ]
  },
  {
    category: "Galleries",
    icon: <Image className="w-4 h-4" />,
    questions: [
      { q: "What's the difference between public and private galleries?", a: "Public galleries appear on your profile for anyone to see. Private galleries are only accessible via a unique magic link that you send to specific clients." },
      { q: "What are the storage and upload limits?", a: "Photo uploads: Plan-based session limits apply. Video hosting: Time-based limits (Starter: 30 min, Pro: 60 min, Studio: 120 min). Founder/Early Access members receive bonus video minutes. Max file size is 50MB per photo. All plans include Cloudflare R2 storage for fast, reliable delivery." },
      { q: "Is storage truly unlimited?", a: "No. 360Lumina uses fair usage policies. Photo storage is subject to plan-based session limits, and video hosting has time-based limits per plan. This ensures reliable performance for all photographers." }
    ]
  },
  {
    category: "Subscriptions",
    icon: <CreditCard className="w-4 h-4" />,
    questions: [
      { q: "Can I cancel my subscription?", a: "Yes, you can cancel anytime from Dashboard → Billing. You'll maintain access until the end of your current billing period and won't be charged again." },
      { q: "What happens if I downgrade?", a: "Downgrades take effect at the end of your current billing period. You'll keep access to current features until then." },
      { q: "Can I reactivate after canceling?", a: "Yes! If you cancel but change your mind before your subscription ends, you can reactivate from the Billing page to continue without interruption." }
    ]
  }
];

const PUBLIC_FAQS = [
  {
    category: "General",
    icon: <HelpCircle className="w-4 h-4" />,
    questions: [
      { q: "What is 360Lumina?", a: "360Lumina is an all-in-one platform for photographers to showcase work, manage bookings, deliver photos, and get paid." },
      { q: "Can I try it out before committing?", a: "Absolutely! Every new account starts with a 14-day free trial of the Pro plan—no credit card required. You'll have access to all Pro plan features so you can see how 360Lumina fits your workflow." }
    ]
  }
];

const HelpPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [profileStatus, setProfileStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const isLoggedIn = !!user;

  // Fetch subscription plans for dynamic pricing display
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get(`${API}/billing/plans`);
        // Sort plans by price and filter to main tiers
        const mainPlans = response.data
          .filter(p => ['starter', 'pro', 'studio'].includes(p.key))
          .sort((a, b) => a.monthly_price - b.monthly_price);
        setPlans(mainPlans);
      } catch (error) {
        console.error("Failed to fetch plans:", error);
        // Fallback to defaults if API fails
        setPlans([
          { key: 'starter', name: 'Starter', monthly_price: 25, features: ['portfolio_site', 'booking_requests', 'basic_email_notifications'] },
          { key: 'pro', name: 'Pro', monthly_price: 39, features: ['portfolio_site', 'booking_requests', 'calendar_sync', 'watermarking', 'promo_codes'] },
          { key: 'studio', name: 'Studio', monthly_price: 79, features: ['portfolio_site', 'booking_requests', 'calendar_sync', 'watermarking', 'contracts_releases', 'multi_photographer', 'priority_support'] }
        ]);
      }
    };
    fetchPlans();
  }, []);

  // Fetch profile completion status for logged-in users
  useEffect(() => {
    const fetchProfileStatus = async () => {
      if (!isLoggedIn) return;
      try {
        const response = await fetch(`${API}/photographer/profile`, {
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          setProfileStatus({
            hasTagline: !!data.tagline && data.tagline !== "Capturing moments that last a lifetime.",
            hasBio: !!data.bio && data.bio !== "Welcome to my photography page! I'm passionate about capturing beautiful moments.",
            hasSocialLinks: data.social_links && Object.values(data.social_links).some(v => v),
            hasLogo: !!data.logo_url,
            hasPortfolio: !!data.portfolio_album_id,
            hasStripe: data.stripe_connect_status === 'active',
            companyName: data.company_name
          });
        }
      } catch (error) {
        console.error("Failed to fetch profile status:", error);
      }
    };
    fetchProfileStatus();
  }, [isLoggedIn]);

  // Format feature key to readable text
  const formatFeature = (key) => {
    const featureNames = {
      'portfolio_site': 'Portfolio site',
      'booking_requests': 'Booking requests',
      'availability_calendar': 'Availability calendar',
      'basic_email_notifications': 'Email notifications',
      'private_client_galleries_30_day': 'Private galleries (with quotes & invoices)',
      'sell_downloads': 'Sell photo downloads',
      'calendar_sync': 'Google/iCal sync',
      'watermarking': 'Auto watermarking',
      'email_templates_automation': 'Email templates & automation',
      'public_event_galleries': 'Public event galleries',
      'quote_templates': 'Reusable quote templates',
      'invoicing': 'Invoice templates & automation',
      'promo_codes': 'Promo codes',
      'multi_photographer': 'Multi-photographer teams',
      'shared_calendars': 'Shared team calendars',
      'team_galleries': 'Team galleries',
      'branding_controls': 'Custom branding',
      'priority_support': 'Priority support',
      'contracts_releases': 'Contracts & e-signatures',
      'client_portal_advanced': 'Advanced client portal',
      'analytics_reporting': 'Analytics & reporting'
    };
    return featureNames[key] || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Generate dynamic plan display data
  const getPlanDisplayData = useMemo(() => {
    if (plans.length === 0) return [];
    
    return plans
      .filter(plan => plan.show_on_pricing_page !== false) // Respect visibility toggle
      .map(plan => {
        // Get top 4-5 features for display
        const displayFeatures = plan.features?.slice(0, 5).map(formatFeature) || [];
        
        // Respect show_price toggle - show "Contact us" if price hidden
        const showPrice = plan.show_price !== false;
        const priceDisplay = showPrice 
          ? `$${plan.monthly_price}/mo` 
          : "Contact us";
        
        // Add "Everything in [previous]" for higher tiers
        if (plan.key === 'studio' && displayFeatures.length > 0) {
          return {
            name: plan.name,
            key: plan.key,
            price: priceDisplay,
            showPrice: showPrice,
            isEarlyAccess: plan.is_early_access,
            features: ['Everything in Pro', ...displayFeatures.slice(0, 4)]
          };
        }
        
        return {
          name: plan.name,
          key: plan.key,
          price: priceDisplay,
          showPrice: showPrice,
          isEarlyAccess: plan.is_early_access,
          features: displayFeatures
        };
      });
  }, [plans]);

  // Get platform fees by tier
  const getPlatformFees = useMemo(() => {
    const feeMap = { starter: 5, pro: 3, studio: 3, legacy: 3 };
    return plans.map(p => `${p.name} (${feeMap[p.key] || 3}%)`).join(', ');
  }, [plans]);

  // Get storage info
  const getStorageInfo = useMemo(() => {
    const starter = plans.find(p => p.key === 'starter');
    return starter ? 'Starter: 5 galleries total. Pro and Studio: Unlimited galleries.' : 'Storage varies by plan.';
  }, [plans]);

  // Dynamic FAQs that use plan data
  const dynamicFaqs = useMemo(() => {
    const baseFaqs = [
      {
        category: "Getting Started",
        icon: <Rocket className="w-4 h-4" />,
        questions: [
          { q: "How do I set my availability for bookings?", a: "Go to Dashboard → Calendar to set your available time slots. Click on any date to add or remove availability. You can also connect Google Calendar for automatic two-way sync." },
          { q: "What happens when my trial ends?", a: "After your 14-day trial, you'll be automatically billed at your currently subscribed tier. You can change or cancel your subscription anytime before the trial ends in Dashboard → Billing." },
          { q: "Can I change my profile URL?", a: "Only before you publish for the first time. After publishing, your URL becomes permanent to prevent broken links." }
        ]
      },
      {
        category: "Payments",
        icon: <CreditCard className="w-4 h-4" />,
        questions: [
          { q: "How do I get paid?", a: "Connect your Stripe account in Dashboard → Payments. Once connected, you can create invoices, accept payments, and sell photos. Payments go directly to your bank account." },
          { q: "What are the fees?", a: "360Lumina's platform fee is inclusive of Stripe processing fees. You are never charged a separate Stripe fee on top of the platform fee. Platform fees by tier: Starter (5%), Pro (3%), Studio (3%)." },
          { q: "How does the quote and invoice process work?", a: "The flow is: 1) Create & send a Quote, 2) Client accepts the Quote, 3) An Invoice is automatically generated. You can also create ad-hoc charges for additional services outside of quotes." }
        ]
      },
      {
        category: "Galleries & Storage",
        icon: <Image className="w-4 h-4" />,
        questions: [
          { q: "What's the difference between public and private galleries?", a: "Public galleries appear on your profile for anyone to see. Private galleries are only accessible via a unique magic link that you send to specific clients." },
          { q: "What are the storage and upload limits?", a: "Photo uploads: Plan-based session limits apply. Video hosting: Time-based limits (Starter: 30 min, Pro: 60 min, Studio: 120 min). Founder/Early Access members receive bonus video minutes. Max file size is 50MB per photo." },
          { q: "Is storage truly unlimited?", a: "No. 360Lumina uses fair usage policies. Photo storage is subject to plan-based session limits, and video hosting has time-based limits per plan. This ensures reliable performance for all photographers." }
        ]
      },
      {
        category: "Subscriptions",
        icon: <CreditCard className="w-4 h-4" />,
        questions: [
          { q: "Can I cancel my subscription?", a: "Yes, you can cancel anytime from Dashboard → Billing. You'll maintain access until the end of your current billing period and won't be charged again." },
          { q: "What happens if I downgrade?", a: "Downgrades take effect at the end of your current billing period. You'll keep access to current features until then." },
          { q: "Can I reactivate after canceling?", a: "Yes! If you cancel but change your mind before your subscription ends, you can reactivate from the Billing page to continue without interruption." }
        ]
      }
    ];
    return baseFaqs;
  }, []);

  // Scroll to top when article changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedArticle]);

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (hash && HELP_ARTICLES[hash] && hash !== selectedArticle) {
      // Using requestAnimationFrame to avoid synchronous setState warning
      requestAnimationFrame(() => setSelectedArticle(hash));
    }
  }, [location, selectedArticle]);

  const faqs = isLoggedIn ? dynamicFaqs : PUBLIC_FAQS;

  const topicCards = isLoggedIn ? [
    { id: "getting-started", ...HELP_ARTICLES["getting-started"] },
    { id: "calendar", ...HELP_ARTICLES["calendar"] },
    { id: "galleries", ...HELP_ARTICLES["galleries"] },
    { id: "payments", ...HELP_ARTICLES["payments"] },
    { id: "contracts", ...HELP_ARTICLES["contracts"] },
    { id: "account", ...HELP_ARTICLES["account"] }
  ] : [];

  // Render step content with visual formatting
  const renderStep = (step, stepIndex) => (
    <motion.div
      key={stepIndex}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: stepIndex * 0.1 }}
      className="space-y-4"
    >
      {/* Step Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary">
          {step.icon}
        </div>
        <h3 className="text-xl font-medium">{step.title}</h3>
        {step.badge && (
          <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">{step.badge}</Badge>
        )}
      </div>

      {/* Highlights Grid */}
      {step.highlights && (
        <div className="grid sm:grid-cols-2 gap-3">
          {step.highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
              <div className="text-primary">{h.icon}</div>
              <span className="text-sm">{h.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Checklist - with dynamic completion status for logged in users */}
      {step.checklist && (
        <div className="space-y-2">
          {step.checklist.map((item, i) => {
            // Determine completion status based on item label
            let isComplete = false;
            if (profileStatus) {
              const labelLower = item.label.toLowerCase();
              if (labelLower.includes('brand name') || labelLower === 'brand name') {
                isComplete = !!profileStatus.companyName;
              } else if (labelLower.includes('profile photo') || labelLower.includes('logo') || labelLower === 'photo') {
                isComplete = profileStatus.hasLogo;
              } else if (labelLower.includes('tagline')) {
                isComplete = profileStatus.hasTagline;
              } else if (labelLower.includes('bio')) {
                isComplete = profileStatus.hasBio;
              } else if (labelLower.includes('social')) {
                isComplete = profileStatus.hasSocialLinks;
              } else if (labelLower.includes('portfolio')) {
                isComplete = profileStatus.hasPortfolio;
              } else if (labelLower.includes('availability')) {
                isComplete = true; // Default to true as we don't track this
              }
            }
            
            return (
              <div key={i} className={`flex items-start gap-3 p-3 bg-card border rounded-lg ${isComplete && profileStatus ? 'border-green-500/30 bg-green-500/5' : 'border-border'}`}>
                <CheckCircle className={`w-5 h-5 mt-0.5 ${
                  isComplete && profileStatus 
                    ? 'text-green-500' 
                    : item.required 
                      ? 'text-primary' 
                      : 'text-muted-foreground'
                }`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{item.label}</span>
                    {item.required && !isComplete && <span className="text-red-400 ml-1">*</span>}
                    {isComplete && profileStatus && (
                      <Badge variant="outline" className="text-xs border-green-500/30 text-green-500 bg-green-500/10">Done</Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Numbered Steps */}
      {step.numbered && (
        <div className="space-y-2">
          {step.numbered.map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center text-sm font-bold text-primary flex-shrink-0">
                {i + 1}
              </div>
              <span className="text-muted-foreground">{item}</span>
            </div>
          ))}
        </div>
      )}

      {/* Options/Choices */}
      {step.options && (
        <div className="grid sm:grid-cols-2 gap-3">
          {step.options.map((opt, i) => (
            <div key={i} className={`p-4 bg-card border border-border rounded-lg ${opt.color || ''}`}>
              <div className="flex items-center gap-2 mb-1">
                <span className={opt.color || 'text-primary'}>{opt.icon}</span>
                <span className="font-medium">{opt.label}</span>
              </div>
              <p className="text-sm text-muted-foreground">{opt.desc}</p>
            </div>
          ))}
        </div>
      )}

      {/* Features List */}
      {step.features && (
        <div className="grid sm:grid-cols-2 gap-2">
          {step.features.map((f, i) => (
            <div key={i} className="flex items-center gap-2 text-sm">
              <span className="text-green-400">{f.icon}</span>
              <span className="text-muted-foreground">{f.text}</span>
            </div>
          ))}
        </div>
      )}

      {/* Specs Table */}
      {step.specs && (
        <div className="bg-muted/30 rounded-lg overflow-hidden">
          {step.specs.map((spec, i) => (
            <div key={i} className={`flex justify-between p-3 ${i > 0 ? 'border-t border-border' : ''}`}>
              <span className="text-muted-foreground">{spec.label}</span>
              <span className="font-medium">{spec.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Cards Grid */}
      {step.cards && (
        <div className="grid sm:grid-cols-2 gap-4">
          {step.cards.map((card, i) => (
            <Card key={i} className="bg-card border-border">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{card.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-1">
                  {card.items.map((item, j) => (
                    <li key={j} className="text-sm text-muted-foreground flex items-center gap-2">
                      <ChevronRight className="w-3 h-3 text-primary" />
                      {item}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Plans Comparison - Use dynamic data with show_price respect */}
      {(step.plans || step.dynamic_plans) && (
        <div className="grid sm:grid-cols-3 gap-4">
          {getPlanDisplayData.map((plan, i) => (
            <Card key={plan.key || i} className={`bg-card border-border ${plan.key === 'pro' ? 'ring-2 ring-primary' : ''} ${plan.key === 'studio' ? 'border-amber-500/30' : ''}`}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-2 mb-1">
                  {plan.key === 'studio' && <Crown className="w-4 h-4 text-amber-400" />}
                  <h4 className="font-medium text-lg">{plan.name}</h4>
                </div>
                {plan.showPrice ? (
                  <p className="text-2xl font-bold text-primary mb-4">{plan.price}</p>
                ) : (
                  <div className="mb-4">
                    <p className="text-lg text-muted-foreground">Contact us for</p>
                    <p className="text-lg text-amber-400 font-medium">early access pricing</p>
                  </div>
                )}
                <ul className="space-y-2">
                  {plan.features.map((f, j) => (
                    <li key={j} className="text-sm text-muted-foreground flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      {f}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Placeholders */}
      {step.placeholders && (
        <div className="flex flex-wrap gap-2">
          {step.placeholders.map((p, i) => (
            <code key={i} className="px-2 py-1 bg-muted rounded text-sm text-primary">{p}</code>
          ))}
        </div>
      )}

      {/* Settings List */}
      {step.settings && (
        <ul className="space-y-1">
          {step.settings.map((s, i) => (
            <li key={i} className="text-sm text-muted-foreground flex items-center gap-2">
              <ChevronRight className="w-3 h-3 text-primary" />
              {s}
            </li>
          ))}
        </ul>
      )}

      {/* Statuses */}
      {step.statuses && (
        <div className="flex gap-4">
          {step.statuses.map((s, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className={s.color}>{s.icon}</span>
              <div>
                <span className="font-medium text-sm">{s.label}</span>
                <p className="text-xs text-muted-foreground">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Highlight Box */}
      {step.highlight && (
        <div className="p-4 bg-primary/10 border border-primary/20 rounded-lg">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-primary">{step.highlight.icon}</span>
            <span className="font-medium">{step.highlight.title}</span>
          </div>
          <p className="text-sm text-muted-foreground">{step.highlight.text}</p>
        </div>
      )}

      {/* Tip Box */}
      {step.tip && (
        <div className="flex items-start gap-3 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
          <Lightbulb className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-yellow-200">{step.tip}</p>
        </div>
      )}

      {/* Warning Box */}
      {step.warning && (
        <div className="flex items-start gap-3 p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
          <Shield className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-200">{step.warning}</p>
        </div>
      )}

      {/* Note */}
      {step.note && (
        <p className="text-sm text-muted-foreground italic">{step.note}</p>
      )}

      {/* Action Button */}
      {step.action && (
        <Link to={step.action.link}>
          <Button className="mt-2">
            {step.action.label}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      )}
    </motion.div>
  );

  // Article Detail View
  if (selectedArticle && HELP_ARTICLES[selectedArticle]) {
    const article = HELP_ARTICLES[selectedArticle];
    return (
      <div className="min-h-screen bg-background" data-testid="help-article">
        <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <button 
              onClick={() => setSelectedArticle(null)}
              className="flex items-center gap-2 text-muted-foreground hover:text-white transition"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Help Center
            </button>
          </div>
        </header>

        <main className="max-w-4xl mx-auto px-6 py-12">
          {/* Article Header */}
          <div className="flex items-center gap-4 mb-8">
            <div className={`p-4 rounded-xl ${article.bgColor}`}>
              <span className={article.color}>{article.icon}</span>
            </div>
            <div>
              <h1 className="text-3xl font-serif">{article.title}</h1>
              <p className="text-muted-foreground">{article.steps?.length || 0} sections</p>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-12">
            {article.steps?.map((step, index) => (
              <Card key={index} className="bg-card border-border overflow-hidden">
                <CardContent className="pt-6">
                  {renderStep(step, index)}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Navigation */}
          <div className="mt-12 pt-8 border-t border-border flex justify-between">
            <Button variant="outline" onClick={() => setSelectedArticle(null)}>
              <ChevronLeft className="w-4 h-4 mr-2" />
              All Topics
            </Button>
            <Link to="/dashboard">
              <Button>
                Go to Dashboard
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // Main Help Center
  return (
    <div className="min-h-screen bg-background" data-testid="help-page">
      <header className="border-b border-border bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            {isLoggedIn ? (
              <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-white transition">
                <ArrowLeft className="w-4 h-4" />
                Back to Dashboard
              </Link>
            ) : (
              <Link to="/" className="flex items-center gap-3">
                <Camera className="w-8 h-8 text-primary" />
                <span className="text-xl font-serif">360Lumina</span>
              </Link>
            )}
            {!isLoggedIn && (
              <Link to="/login">
                <Button>Get Started</Button>
              </Link>
            )}
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="py-12 md:py-16 px-6 border-b border-border">
        <div className="max-w-6xl mx-auto text-center">
          <HelpCircle className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="text-3xl md:text-4xl font-serif mb-3">
            {isLoggedIn ? "How can we help?" : "Help Center"}
          </h1>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8">
            {isLoggedIn ? "Guides and tutorials to help you succeed" : "Learn about 360Lumina"}
          </p>
          
          <div className="max-w-md mx-auto relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Topic Cards */}
        {isLoggedIn && (
          <section className="mb-16">
            <h2 className="text-xl font-medium mb-6">Browse Topics</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {topicCards.map((topic, index) => (
                <motion.div
                  key={topic.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <Card 
                    className="bg-card border-border h-full hover:border-primary/30 transition-all cursor-pointer group"
                    onClick={() => setSelectedArticle(topic.id)}
                  >
                    <CardContent className="pt-6">
                      <div className={`w-14 h-14 rounded-xl ${topic.bgColor} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                        <span className={topic.color}>{topic.icon}</span>
                      </div>
                      <h3 className="text-lg font-medium mb-2">{topic.title}</h3>
                      <p className="text-sm text-muted-foreground mb-3">{topic.steps?.length || 0} guides</p>
                      <div className="flex items-center text-primary text-sm">
                        Read guides <ArrowRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </section>
        )}

        {/* Quick Actions */}
        {isLoggedIn && (
          <section className="mb-16">
            <h2 className="text-xl font-medium mb-6">Quick Actions</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                { icon: <Image className="w-5 h-5" />, label: "Create Gallery", link: "/dashboard/galleries/new", color: "text-green-400" },
                { icon: <Calendar className="w-5 h-5" />, label: "Set Availability", link: "/dashboard/calendar", color: "text-blue-400" },
                { icon: <CreditCard className="w-5 h-5" />, label: "Connect Stripe", link: "/dashboard/payments", color: "text-yellow-400" },
                { icon: <User className="w-5 h-5" />, label: "Edit Profile", link: "/dashboard/profile", color: "text-purple-400" }
              ].map((action, idx) => (
                <Link key={idx} to={action.link}>
                  <Card className="bg-card border-border hover:border-primary/30 transition-colors">
                    <CardContent className="p-4 flex items-center gap-3">
                      <div className={`p-2 bg-muted rounded-lg ${action.color}`}>{action.icon}</div>
                      <span className="font-medium">{action.label}</span>
                      <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* FAQs */}
        <section className="mb-16">
          <h2 className="text-xl font-medium mb-6">Frequently Asked Questions</h2>
          {faqs.map((category, catIdx) => (
            <div key={catIdx} className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                <span className="text-muted-foreground">{category.icon}</span>
                <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wider">
                  {category.category}
                </h3>
              </div>
              <div className="space-y-3">
                {category.questions.map((faq, faqIdx) => {
                  const key = `${catIdx}-${faqIdx}`;
                  return (
                    <Card key={key} className="bg-card border-border">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === key ? null : key)}
                        className="w-full p-4 flex items-center justify-between text-left"
                      >
                        <span className="font-medium pr-4">{faq.q}</span>
                        <ChevronDown className={`w-5 h-5 text-muted-foreground transition-transform ${expandedFaq === key ? 'rotate-180' : ''}`} />
                      </button>
                      {expandedFaq === key && (
                        <div className="px-4 pb-4">
                          <p className="text-muted-foreground text-sm">{faq.a}</p>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            </div>
          ))}
        </section>

        {/* Contact */}
        <section className="text-center">
          <Card className="bg-primary/5 border-primary/20 inline-block w-full max-w-lg">
            <CardContent className="pt-6 pb-6">
              <Mail className="w-10 h-10 text-primary mx-auto mb-4" />
              <h3 className="text-xl font-medium mb-2">Still need help?</h3>
              <p className="text-muted-foreground mb-6">We&apos;ll get back to you within 24 hours.</p>
              <a href="mailto:support@360lumina.com">
                <Button size="lg">
                  <Mail className="w-4 h-4 mr-2" />
                  Contact Support
                </Button>
              </a>
            </CardContent>
          </Card>
        </section>
      </main>

      {!isLoggedIn && (
        <footer className="border-t border-border py-8 px-6">
          <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5 text-primary" />
              <span className="font-serif">360Lumina</span>
            </div>
            <div className="flex gap-6 text-sm text-muted-foreground">
              <Link to="/features" className="hover:text-white">Features</Link>
              <Link to="/pricing" className="hover:text-white">Pricing</Link>
              <Link to="/login" className="hover:text-white">Login</Link>
            </div>
          </div>
        </footer>
      )}
    </div>
  );
};

export default HelpPage;
